/**
 * Chime attendee identities (P7-A design Decision 39), shared by the Chime bots and the call pages.
 *
 * An attendee's `ExternalUserId` is `<base id>#<display name>`; Chime gives a screen share the sharer's id plus
 * `#content`. Every attendee carries a name, so an id with two `#` parts is a screen share.
 *   - `practitioner:<16 hex>` / `patient:<16 hex>`: a FHIR participant (per-meeting hash of the reference).
 *   - `guest:<12 hex>`: a link joiner (prefix of their join-key handle), or `guest:<12 hex>:<12 hex>` when the
 *     guest stands for a participant (prefix of that participant's per-meeting hash).
 * The longest is 62 characters, inside Chime's 64. Names are visible to everyone in the call and travel in
 * Chime's meeting events; Chime keeps them only for the meeting's life.
 *
 * Chime adds a running media capture pipeline to the meeting as an attendee whose `ExternalUserId` starts with
 * `aws:MediaPipeline-` (confirmed on a live meeting, 2026-11-03), so "is this session recording" is a roster
 * question every participant can answer directly.
 */

export const CHIME_REF_HASH_HEX = 16;
export const CHIME_GUEST_ID_HEX = 12;
export const CHIME_DISPLAY_NAME_MAX = 30;
export const CHIME_RECORDER_EXTERNAL_USER_ID_PREFIX = 'aws:MediaPipeline-';

export type ChimeAttendeeKind = 'practitioner' | 'patient' | 'guest';

export interface ParsedChimeAttendee {
  kind: ChimeAttendeeKind;
  /** The base id, without the name or `#content`. */
  baseId: string;
  name?: string;
  /** A screen share's attendee, named after its sharer. */
  content: boolean;
  /** `practitioner`/`patient`: the reference hash. `guest`: the handle prefix. */
  id: string;
  /** `guest` standing for a participant: that participant's hash prefix. */
  participantHash?: string;
}

/** True for the media capture pipeline Chime adds to a recording meeting. */
export function isChimeRecorderExternalUserId(externalUserId: string | undefined): boolean {
  return Boolean(externalUserId?.startsWith(CHIME_RECORDER_EXTERNAL_USER_ID_PREFIX));
}

/** True while any attendee in the roster is the recorder. */
export function chimeRosterIsRecording(externalUserIds: Iterable<string | undefined>): boolean {
  for (const id of externalUserIds) if (isChimeRecorderExternalUserId(id)) return true;
  return false;
}

/**
 * Characters Chime accepts in an `ExternalUserId` (CreateAttendee pattern `[-_&@+=,(){}\[\]\/«».:|'"#a-zA-Z0-9À-ÿ\s]*`),
 * minus `#`, which separates the name. Anything else makes CreateAttendee fail, so the person could never join.
 */
const CHIME_NAME_DISALLOWED = /[^-_&@+=,(){}[\]/«».:|'"a-zA-Z0-9À-ÿ ]/g;

/**
 * A display name Chime accepts: accents outside Latin-1 are folded (`Nguyễn` → `Nguyen`, `Łukasz` → `Lukasz`), curly
 * quotes become straight ones, any other character becomes a space, spaces collapse, at most
 * {@link CHIME_DISPLAY_NAME_MAX} characters, and `fallback` when nothing is left.
 */
export function chimeDisplayName(name: string | undefined, fallback: string): string {
  const clean = (name ?? '')
    .replace(/[\u2018\u2019\u201b\u2032]/g, "'")
    .replace(/[\u201c\u201d\u201f\u2033]/g, '"')
    .replace(/[Łł]/g, (c) => (c === 'Ł' ? 'L' : 'l'))
    .replace(/[Đđ]/g, (c) => (c === 'Đ' ? 'D' : 'd'))
    .split('')
    // Keep Latin-1 letters as they are (Chime accepts them); fold anything else to its base letter where it has one.
    .map((c) => (/[À-ÿ]/.test(c) ? c : c.normalize('NFKD').replace(/[\u0300-\u036f]/g, '')))
    .join('')
    .replace(CHIME_NAME_DISALLOWED, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, CHIME_DISPLAY_NAME_MAX)
    .trim();
  return clean || fallback;
}

export function chimeParticipantBaseId(kind: 'practitioner' | 'patient', refHash: string): string {
  return `${kind}:${refHash.slice(0, CHIME_REF_HASH_HEX)}`;
}

export function chimeGuestBaseId(handle: string, participantHash?: string): string {
  const id = `guest:${handle.slice(0, CHIME_GUEST_ID_HEX)}`;
  return participantHash ? `${id}:${participantHash.slice(0, CHIME_GUEST_ID_HEX)}` : id;
}

/** `<baseId>#<name>`; `name` is cleaned with {@link chimeDisplayName}. */
export function chimeExternalUserId(baseId: string, name: string | undefined, fallback: string): string {
  return `${baseId}#${chimeDisplayName(name, fallback)}`;
}

const PARTICIPANT_ID = new RegExp(`^(practitioner|patient):([0-9a-f]{${CHIME_REF_HASH_HEX}})$`);
const GUEST_ID = new RegExp(`^guest:([0-9a-f]{${CHIME_GUEST_ID_HEX}})(?::([0-9a-f]{${CHIME_GUEST_ID_HEX}}))?$`);

/** Undefined for the recorder, Chime's own attendees, or anything not minted by the Chime bots. */
export function parseChimeExternalUserId(externalUserId: string | undefined): ParsedChimeAttendee | undefined {
  if (!externalUserId || isChimeRecorderExternalUserId(externalUserId)) return undefined;
  const parts = externalUserId.split('#');
  const content = parts.length === 3 && parts[2] === 'content';
  if (parts.length > 3 || (parts.length === 3 && !content)) return undefined;
  const [baseId, name] = parts;
  const participant = PARTICIPANT_ID.exec(baseId);
  if (participant) {
    return { kind: participant[1] as 'practitioner' | 'patient', baseId, id: participant[2], content, ...(name ? { name } : {}) };
  }
  const guest = GUEST_ID.exec(baseId);
  if (guest) {
    return {
      kind: 'guest',
      baseId,
      id: guest[1],
      content,
      ...(guest[2] ? { participantHash: guest[2] } : {}),
      ...(name ? { name } : {}),
    };
  }
  return undefined;
}

/** The base id of an attendee (name and `#content` removed), or undefined when it is not one of ours. */
export function chimeAttendeeBaseId(externalUserId: string | undefined): string | undefined {
  return parseChimeExternalUserId(externalUserId)?.baseId;
}
